import { useState, type ReactNode } from "react";
import { Check, ChevronDown, Sparkles, TriangleAlert, X } from "lucide-react";

import type { GroupCount } from "@/lib/integrations/fieldGroups";
import {
  fromSummary,
  isMapped,
  showValue,
  valueForType,
  type ColumnInfo,
  type FieldSetting,
  type From,
  type FromKind,
} from "@/lib/integrations/settings";

import { ColumnTree } from "./ColumnTree";
import { Button, Field, Input, Segmented } from "./controls";
import { TonePill } from "./HealthPill";
import styles from "./integrations.module.css";
import { Floating } from "./Floating";

/**
 * The heading row of one group of fields: its name, how much is mapped as a
 * number and a bar, and whether a needed field is still missing. Opens and
 * shuts the rows under it.
 */
export function FieldGroupRow({
  title,
  count,
  list,
  open,
  onToggle,
  columns,
}: {
  title: string;
  count: GroupCount;
  list: boolean;
  open: boolean;
  onToggle: () => void;
  columns: number;
}) {
  const share = count.total > 0 ? count.mapped / count.total : 0;
  return (
    <tr className={styles.groupRow}>
      <td colSpan={columns}>
        <button type="button" className={open ? styles.groupToggle : `${styles.groupToggle} ${styles.groupShut}`} aria-expanded={open} onClick={onToggle}>
          <span className={styles.groupChevron}>
            <ChevronDown size={16} aria-hidden />
          </span>
          <span className={styles.groupTitle}>{title}</span>
          <span className={styles.groupCount}>
            {count.mapped} of {count.total} mapped
          </span>
          <span className={styles.meter} aria-hidden>
            <span className={styles.meterFill} style={{ width: `${share * 100}%` }} />
          </span>
          {count.requiredMissing > 0 ? (
            <TonePill tone="warning">
              {count.requiredMissing} needed {count.requiredMissing === 1 ? "field" : "fields"} not mapped
            </TonePill>
          ) : count.required > 0 ? (
            <TonePill tone="good">Needed fields mapped</TonePill>
          ) : null}
          {list && <span className={styles.listNote}>One set for each entry in a list</span>}
        </button>
      </td>
    </tr>
  );
}

export type FieldInfo = { key: string; label: string; type: string; required: boolean; hint: string; many?: boolean };

function FromEditor({
  field,
  from,
  columns,
  total,
  onChange,
  onClear,
  onClose,
}: {
  field: FieldInfo;
  from: From;
  columns: ColumnInfo[];
  total: number;
  onChange: (from: From) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<FromKind>(from.kind);
  const text = (value: unknown) => (value === null || value === undefined ? "" : String(value));
  return (
    <div className={`${styles.editor} ${styles.editorWide}`}>
      <div className={styles.editorHead}>
        <span>Where {field.label} comes from</span>
        <Button tone="subtle" icon aria-label="Close" onClick={onClose}>
          <X size={15} />
        </Button>
      </div>
      {field.hint && <p className={styles.muted} style={{ margin: 0 }}>{field.hint}</p>}
      <Segmented<FromKind>
        name="Kind of source"
        value={kind}
        options={[
          { value: "column", label: "One column" },
          { value: "first", label: "First with a value" },
          { value: "join", label: "Text from columns" },
          { value: "fixed", label: "A fixed value" },
        ]}
        onChange={(next) => {
          setKind(next);
          if (next === "column") onChange({ kind: next, column: from.column ?? from.columns?.[0] ?? null });
          if (next === "first") onChange({ kind: next, columns: from.columns?.length ? from.columns : from.column ? [from.column] : [] });
          if (next === "join") onChange({ kind: next, template: from.template ?? (from.column ? `{${from.column}}` : "") });
          if (next === "fixed") onChange({ kind: next, value: from.value ?? null });
        }}
      />
      {kind === "column" && (
        <ColumnTree
          columns={columns}
          total={total}
          selected={from.column}
          onPick={(path) => {
            onChange({ kind: "column", column: path });
            onClose();
          }}
        />
      )}
      {kind === "first" && (
        <>
          <p className={styles.muted} style={{ margin: 0 }}>
            In order: {(from.columns ?? []).length ? (from.columns ?? []).join(" → ") : "pick columns below"}
          </p>
          {(from.columns ?? []).length > 0 && (
            <div className={styles.chips}>
              {(from.columns ?? []).map((path) => (
                <span key={path} className={styles.stepChip}>
                  <span className={styles.stepChipMain}>{path}</span>
                  <button
                    type="button"
                    className={styles.stepChipX}
                    aria-label={`Remove ${path}`}
                    onClick={() => onChange({ kind: "first", columns: (from.columns ?? []).filter((one) => one !== path) })}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <ColumnTree
            columns={columns}
            total={total}
            onPick={(path) => {
              if (!(from.columns ?? []).includes(path)) onChange({ kind: "first", columns: [...(from.columns ?? []), path] });
            }}
          />
        </>
      )}
      {kind === "join" && (
        <>
          <Field label="Text" hint="Put column names in braces, e.g. {Disc}:{Key}. Pick a column below to add it.">
            <Input value={from.template ?? ""} onChange={(event) => onChange({ kind: "join", template: event.target.value })} />
          </Field>
          <ColumnTree
            columns={columns}
            total={total}
            onPick={(path) => onChange({ kind: "join", template: `${from.template ?? ""}{${path}}` })}
          />
        </>
      )}
      {kind === "fixed" && (
        <Field label="Value" hint={`Every row gets this. The field is ${field.type}.`}>
          <Input
            value={text(from.value)}
            onChange={(event) => onChange({ kind: "fixed", value: valueForType(event.target.value, field.type) })}
          />
        </Field>
      )}
      <div className={styles.actions}>
        <Button tone="primary" onClick={onClose}>
          Done
        </Button>
        <Button tone="subtle" onClick={() => { onClear(); onClose(); }}>
          Not mapped
        </Button>
      </div>
    </div>
  );
}

/**
 * One field of the import: our field · from · steps · preview · filled.
 *
 * A required field with no value is shown in warning tone, with the reason in
 * words beside it.
 */
export function FieldMapRow({
  field,
  label,
  setting,
  suggested = false,
  columns,
  total,
  preview,
  filled,
  problem,
  steps,
  onChange,
  onConfirm,
  disabled = false,
}: {
  field: FieldInfo;
  /** The label to show, when the group heading already says part of it. */
  label?: string;
  setting: FieldSetting | undefined;
  /** A guess not confirmed yet. */
  suggested?: boolean;
  columns: ColumnInfo[];
  total: number;
  /** The value on the sample row, after every step. */
  preview: { value: unknown; problem: string | null } | null;
  /** How many kept rows have a value, of how many. */
  filled: { count: number; of: number } | null;
  /** A sentence about this field, shown in warning tone. */
  problem?: string | null;
  /** The step chips, built by the caller so they can read previews. */
  steps: ReactNode;
  onChange: (setting: FieldSetting | undefined) => void;
  onConfirm?: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const mapped = isMapped(setting);
  const warn = Boolean(problem) || (field.required && !mapped);
  const share = filled && filled.of > 0 ? Math.round((filled.count / filled.of) * 100) : null;
  return (
    <tr className={warn ? styles.rowWarn : undefined}>
      <td>
        <div className={styles.fieldName}>
          <span className={styles.fieldLabel} title={field.hint || field.label}>
            {label ?? field.label}
            {field.required && (
              <span className={styles.req} aria-label="required">
                *
              </span>
            )}
          </span>
          <span className={styles.fieldKey}>{field.key}</span>
          {suggested && (
            <span className={styles.inline}>
              <span className={styles.suggested}>
                <Sparkles size={11} aria-hidden /> Suggested
              </span>
              {onConfirm && (
                <button type="button" className={styles.linkish} onClick={onConfirm} disabled={disabled}>
                  <Check size={12} aria-hidden /> Confirm
                </button>
              )}
            </span>
          )}
        </div>
      </td>
      <td>
        <Floating
          open={open}
          onClose={() => setOpen(false)}
          wide
          trigger={
            <button
              type="button"
              className={styles.fromButton}
              disabled={disabled}
              onClick={() => setOpen(!open)}
              aria-label={`Where ${field.label} comes from: ${mapped ? fromSummary(setting?.from) : "not mapped"}`}
            >
              <span className={mapped ? styles.mono : styles.fromEmpty}>{mapped ? fromSummary(setting?.from) : "(not mapped)"}</span>
            </button>
          }
        >
          {open && (
            <FromEditor
              field={field}
              from={setting?.from ?? { kind: "column", column: null }}
              columns={columns}
              total={total}
              onChange={(from) => onChange({ from, steps: setting?.steps ?? [] })}
              onClear={() => onChange(undefined)}
              onClose={() => setOpen(false)}
            />
          )}
        </Floating>
      </td>
      <td>{mapped ? steps : <span className={styles.faint}>—</span>}</td>
      <td className={styles.preview}>
        {!mapped ? (
          <span className={styles.faint}>—</span>
        ) : preview === null ? (
          <span className={styles.faint}>…</span>
        ) : preview.problem ? (
          <span className={styles.badText}>
            <TriangleAlert size={12} aria-hidden /> {preview.problem}
          </span>
        ) : (
          <span className={styles.mono}>{showValue(preview.value, 60)}</span>
        )}
        {problem && (
          <div className={styles.warnText} style={{ marginTop: 2 }}>
            <TriangleAlert size={12} aria-hidden /> {problem}
          </div>
        )}
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {!mapped || share === null ? <span className={styles.faint}>—</span> : <span className={share < 100 && field.required ? styles.warnText : undefined}>{share}%</span>}
      </td>
    </tr>
  );
}
